import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button } from '@ui/components/Button'
import { PopoverInput } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { EmailPattern } from '@utils/formValidation'
import { isEmailValid } from '@utils/validations'

import { ClientAreaLanguageDto } from '@model/ClientAreaLanguageDto'
import { CountryPreferenceStrippedDto } from '@model/CountryDto'
import { LeadMembersEnum } from '@model/CreateLead'
import { PhoneNumberType } from '@model/PhoneNumberDto'

import { createFormField } from '@global/formField/FormField'

import { useDropdownState } from '@hooks/useDropdownState'
import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { PhoneNumberInput } from '@shared/components/PhoneNumberInput/PhoneNumberInput'

import styles from './PersonalDetailsStep3Form.module.scss'

const FormField = createFormField<PersonalDetailsStep3FormValues>()

export interface PersonalDetailsStep3FormValues {
  email: string
  languageId: string
  countryCode: string
  phoneNumber: string
  phoneNumberType?: PhoneNumberType
}

const PersonalDetailsStep3FormUI: React.FC<
  FormikProps<PersonalDetailsStep3FormValues> & PersonalDetailsStep3FormProps
> = (props) => {
  const {
    values,
    errors,
    countryId,
    countries = [],
    languages = [],
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = props
  useOnLocaleSwitch(() => props.validateForm())

  const apiClient = useApiClient(ClientApiClient)
  const { t } = useTranslation()
  const locale = useSessionLanguage()

  const [emailValidated, setEmailValidated] = useState(false)
  const [emailDomainError, setEmailDomainError] = useState<string | undefined>(undefined)
  const [phoneNumberError, setPhoneNumberError] = useState<string>()
  const [selectedLanguage, setSelectedLanguage] = useState<string>()

  const [isLoading, setLoading] = useState(false)
  const { isOpen, dropdownRef, closeDropdown, setIsOpen } = useDropdownState()
  const validatePhoneNumber = async (phoneNumber: string, countryCode: string) => {
    try {
      const response = await apiClient.validatePhoneNumber(phoneNumber, countryCode)
      if (response.phoneVerificationRequired) {
        await apiClient.validateBasePhoneNumber(phoneNumber, countryCode)
      }
      setFieldValue('phoneNumberType', response.numberType)
      setPhoneNumberError('')
    } catch (e: unknown) {
      console.error(e)
      setPhoneNumberError(t('Sign up.Please check your phone number'))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout
    if (values.phoneNumber && !errors.phoneNumber) {
      setLoading(true)
      delayDebounceFn = setTimeout(() => {
        validatePhoneNumber(values.phoneNumber, values.countryCode)
      }, 1000)
    }
    if (!values.phoneNumber) {
      setPhoneNumberError('')
    }
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.phoneNumber, errors.phoneNumber, values.countryCode])

  useEffect(() => {
    if (countryId) {
      const country = countries.find((x) => x.country.id === countryId)
      if (country) {
        const lang =
          languages.find((x) => x.id === country.language?.id) ||
          languages.find((x) => x.id === 'en') // fallback to EN

        !values.countryCode && props.setFieldValue('countryCode', country.phoneCode)
        !values.languageId && props.setFieldValue('languageId', lang?.id)
        if (!selectedLanguage) setSelectedLanguage(lang?.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId])

  const handleOnChangeOption = (country: CountryPreferenceStrippedDto) => {
    props.setFieldValue('countryCode', country.phoneCode)
    closeDropdown()
  }

  const handleEmailValidation = async (email: string) => {
    setLoading(true)
    try {
      const valid = await apiClient.validateEmailDomain(email)
      setEmailValidated(valid)
      if (!valid) {
        setFieldTouched('email', true, false)
        setEmailDomainError('Validation.Invalid email address')
      } else {
        setEmailDomainError(undefined)
      }
    } catch (e: unknown) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let delayDebounceFn: NodeJS.Timeout
    const { email } = values
    if (email && isEmailValid(email)) {
      delayDebounceFn = setTimeout(async () => handleEmailValidation(email), 1000)
    }
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.email])

  const initialCountry = useMemo(
    () => countries.find((x) => x.country.id === countryId),
    [countries, countryId]
  )

  return (
    <React.Fragment>
      <Form autoComplete='off' className={styles.form} onSubmit={handleSubmit}>
        <Heading6 medium>{t('Sign up.Phone and Email')}</Heading6>
        <FieldWrapper>
          <div className={classNames({ [styles.itCountryCodeLabel]: locale === 'it' })}>
            <PhoneNumberInput
              handleChangePhoneNumber={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('phoneNumber', e.target.value)
                props.handleChange(e)
              }}
              name='phoneNumber'
              isCountryDropdownOpen={isOpen}
              setIsCountryDropdownOpen={setIsOpen}
              values={values.phoneNumber}
              dropdownRef={dropdownRef}
              countryCode={values.countryCode}
              countries={countries}
              initialCountry={initialCountry}
              handleOnChangeOption={handleOnChangeOption}
              phoneNumberError={phoneNumberError}
            />
          </div>
          <FormField
            name='email'
            label={t('Sign up.Email')}
            type='email'
            error={emailDomainError ? t(emailDomainError) : undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setEmailDomainError(undefined)
              setEmailValidated(false)
              props.handleChange(e)
            }}
            required
            showLabel
          />
        </FieldWrapper>
        <Heading6 medium>
          {t('Sign up.Personal Preferences')}{' '}
          <Tooltip
            content={t(
              "Sign up.Here we'll need some information about your communication languages, wallet preferences and more."
            )}
          >
            <ActionIcon icon={InfoIcon} size='x20' />
          </Tooltip>
        </Heading6>
        <PopoverInput
          name={'languageId'}
          label={t('Sign up.Communication language')}
          value={values.languageId}
          required
          options={languages}
        />
        <div className={styles.button}>
          <Button.Primary
            size='lg'
            type='submit'
            fullWidth
            iconRight={ArrowIcon.Right}
            loading={props.isSubmitting}
            disabled={
              !props.isValid ||
              isLoading ||
              !!phoneNumberError ||
              !!emailDomainError ||
              !emailValidated
            }
          >
            {t('Next')}
          </Button.Primary>
        </div>
      </Form>
    </React.Fragment>
  )
}

export interface PersonalDetailsStep3FormProps {
  phoneCodes: string[]
  countryId?: string
  languages: ClientAreaLanguageDto[]
  countries: CountryPreferenceStrippedDto[]
  onSubmit(values: PersonalDetailsStep3FormValues): void
}

export const PersonalDetailsStep3Form = withFormik<
  PersonalDetailsStep3FormProps,
  PersonalDetailsStep3FormValues
>({
  mapPropsToValues: () => {
    const searchParams = new URLSearchParams(window.location.search)
    const countryCode = searchParams.get(LeadMembersEnum.COUNTRY_CODE)
    return {
      email: searchParams.get(LeadMembersEnum.EMAIL) || '',
      languageId: searchParams.get(LeadMembersEnum.LANGUAGE_ID) || '',
      phoneNumber: searchParams.get(LeadMembersEnum.PHONE_NUMBER) || '',
      countryCode: countryCode ? `+${countryCode}` : '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PersonalDetailsStep3FormValues> = {}
    if (!values.email) {
      errors.email = t('Validation.Required')
    } else if (values.email && !EmailPattern.test(values.email)) {
      errors.email = t('Validation.Invalid email address')
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = t('Validation.Required')
    } else if (values.phoneNumber) {
      if (values.phoneNumber.toString().length < 4) {
        errors.phoneNumber = t('Validation.Invalid phone number. Min 4 numbers.')
      } else if (
        values.phoneNumber &&
        (values.phoneNumber.toString().includes(',') ||
          values.phoneNumber.toString().includes('.') ||
          values.phoneNumber.toString().includes('+'))
      ) {
        errors.phoneNumber = t('Validation.Invalid phone number. Numbers from 0 to 9.')
      }
    }
    return errors
  },
})(PersonalDetailsStep3FormUI)
